import {
  type ApprovalStatus,
  AUTONOMY_LABELS,
  type AutonomyClass,
  isTerminalState,
  type LedgerStatus,
  type ReceiptStatus,
  type SideEffectClass,
  type TaskState,
} from '@agentic/contracts';
import type { ReactNode } from 'react';
import { Badge, type BadgeVariant, type Tone } from './primitives.tsx';

/**
 * Visible friendly label with the raw contract value kept for assistive technology and text search
 * (`sr-only`), or the raw value alone when no label is given.
 */
function Labelled({ label, raw }: { label: ReactNode | undefined; raw: string }) {
  if (label === undefined || label === raw) return <>{raw}</>;
  return (
    <>
      <span>{label}</span>
      {/* The raw value sits in its own element so it can be found by exact text. */}
      <span className="sr-only">
        {' ('}
        <span>{raw}</span>)
      </span>
    </>
  );
}

/**
 * Status colour scale used everywhere a status is shown (task states, approvals, receipts, ledger entries, workflow
 * steps, modules, sources, action status columns). Six kinds, one token each, always with a text label beside the
 * colour:
 *
 * | Kind       | Tone    | Colour | Meaning                                              |
 * | ---------- | ------- | ------ | ---------------------------------------------------- |
 * | `action`   | accent  | blue   | A person must act: approve, answer, unblock          |
 * | `progress` | info    | teal   | The platform is working on it                        |
 * | `done`     | ok      | green  | Finished as intended                                 |
 * | `caution`  | warn    | orange | Finished or stopped with something to check          |
 * | `problem`  | bad     | red    | Failed, rejected or refused                          |
 * | `ended`    | neutral | gray   | Not started, not needed, or ended without effect     |
 */
export type StatusKind = 'action' | 'progress' | 'done' | 'caution' | 'problem' | 'ended';

/** Tone of each status kind (see `StatusKind`). */
export const STATUS_KIND_TONES: Readonly<Record<StatusKind, Tone>> = {
  action: 'accent',
  progress: 'info',
  done: 'ok',
  caution: 'warn',
  problem: 'bad',
  ended: 'neutral',
};

/** Tone of a status kind. */
export function statusTone(kind: StatusKind): Tone {
  return STATUS_KIND_TONES[kind];
}

/** Kind of each task state; matches the task list views (Needs attention, In progress, Done). */
export const TASK_STATE_KINDS: Readonly<Record<TaskState, StatusKind>> = {
  queued: 'progress',
  running: 'progress',
  awaiting_input: 'action',
  awaiting_approval: 'action',
  blocked: 'action',
  reconciling: 'progress',
  compensating: 'progress',
  succeeded: 'done',
  failed: 'problem',
  cancelled: 'ended',
  expired: 'ended',
};

/** Kind of each approval status. */
export const APPROVAL_STATUS_KINDS: Readonly<Record<ApprovalStatus, StatusKind>> = {
  pending: 'action',
  approved: 'done',
  rejected: 'problem',
  expired: 'ended',
  invalidated: 'caution',
};

/** Kind of each tool receipt status (`unknown`: the outcome must be checked). */
export const RECEIPT_STATUS_KINDS: Readonly<Record<ReceiptStatus, StatusKind>> = {
  succeeded: 'done',
  failed: 'problem',
  unknown: 'caution',
  denied: 'problem',
};

/** Kind of each action ledger status. */
export const LEDGER_STATUS_KINDS: Readonly<Record<LedgerStatus, StatusKind>> = {
  intended: 'ended',
  executing: 'progress',
  succeeded: 'done',
  failed: 'problem',
  unknown: 'caution',
  denied: 'problem',
  reconciled: 'done',
};

/**
 * Kind of a free-form lifecycle status string (workflow steps, modules, knowledge sources, uploads, health checks).
 * Unknown values are `ended` (gray), so an unmapped status never looks like success or failure.
 */
export function lifecycleKind(status: string): StatusKind {
  switch (status) {
    case 'succeeded':
    case 'completed':
    case 'ready':
    case 'started':
    case 'active':
    case 'available':
    case 'uploaded':
    case 'verified':
    case 'ok':
      return 'done';
    case 'queued':
    case 'running':
    case 'processing':
    case 'ingesting':
    case 'indexing':
    case 'resumed':
    case 'uploading':
    case 'starting':
    case 'restarting':
    case 'pending':
      return 'progress';
    case 'failed':
    case 'error':
    case 'rejected':
    case 'denied':
      return 'problem';
    case 'unknown':
    case 'stale':
    case 'partially_completed':
    case 'outcome_unknown':
    case 'paused':
    case 'suspended':
    case 'degraded':
      return 'caution';
    default:
      return 'ended';
  }
}

/** Tone per task state, from `TASK_STATE_KINDS`. */
export const TASK_STATE_TONES: Readonly<Record<TaskState, Tone>> = Object.fromEntries(
  Object.entries(TASK_STATE_KINDS).map(([k, v]) => [k, STATUS_KIND_TONES[v]]),
) as Record<TaskState, Tone>;

/**
 * Task state chip. Shows the state name exactly as in the contract vocabulary, or a friendly `label` with the raw
 * state kept as screen-reader text, `data-state` and `title`. The dot gives a shape cue in addition to colour.
 */
export function StateChip({
  state,
  className,
  label,
  variant,
}: {
  state: TaskState;
  className?: string;
  label?: ReactNode;
  /** `text` drops the fill for table cells (dot plus coloured text). */
  variant?: BadgeVariant;
}) {
  const live = !isTerminalState(state) && state !== 'queued';
  return (
    <Badge
      tone={TASK_STATE_TONES[state]}
      mono={label === undefined}
      dot
      pulse={state === 'running' || state === 'reconciling' || state === 'compensating'}
      data-state={state}
      data-live={live || undefined}
      {...(variant ? { variant } : {})}
      title={label === undefined ? undefined : state}
      className={className}
    >
      <Labelled label={label} raw={state} />
    </Badge>
  );
}

const AUTONOMY_TONES: Record<AutonomyClass, Tone> = {
  A0: 'neutral',
  A1: 'neutral',
  A2: 'info',
  A3: 'accent',
  A4: 'bad',
};

/**
 * `A3 approval-bound`. A3 is the enforcement point and carries the accent. With `label` (e.g. "Requires
 * approval") the friendly text shows and the class stays in the title and as screen-reader text.
 */
export function AutonomyBadge({
  value,
  compact,
  label,
}: {
  value: AutonomyClass;
  compact?: boolean;
  label?: ReactNode;
}) {
  if (label !== undefined) {
    return (
      <Badge tone={AUTONOMY_TONES[value]} title={`${value}: ${AUTONOMY_LABELS[value]}`}>
        <span>{label}</span>
        <span className="sr-only">
          {' ('}
          <span>{value}</span>)
        </span>
      </Badge>
    );
  }
  return (
    <Badge tone={AUTONOMY_TONES[value]} mono title={`${value}: ${AUTONOMY_LABELS[value]}`}>
      {value}
      {!compact && <span className="font-sans font-normal">{AUTONOMY_LABELS[value]}</span>}
    </Badge>
  );
}

const SIDE_EFFECT_TONES: Record<SideEffectClass, Tone> = {
  read: 'neutral',
  reversible_write: 'info',
  irreversible_write: 'warn',
  external_communication: 'bad',
};

/** Side-effect class badge, e.g. `irreversible_write`; optional friendly `label` (raw kept as sr-only text). */
export function SideEffectBadge({ value, label }: { value: SideEffectClass; label?: ReactNode }) {
  return (
    <Badge
      tone={SIDE_EFFECT_TONES[value]}
      mono={label === undefined}
      title={label === undefined ? undefined : value}
    >
      <Labelled label={label} raw={value} />
    </Badge>
  );
}

/** Tool receipt status, coloured by `RECEIPT_STATUS_KINDS`. */
export function ReceiptStatusBadge({ value }: { value: ReceiptStatus }) {
  return (
    <Badge tone={STATUS_KIND_TONES[RECEIPT_STATUS_KINDS[value]]} mono dot>
      {value}
    </Badge>
  );
}

/** Action ledger status, coloured by `LEDGER_STATUS_KINDS`. */
export function LedgerStatusBadge({ value }: { value: LedgerStatus }) {
  return (
    <Badge tone={STATUS_KIND_TONES[LEDGER_STATUS_KINDS[value]]} mono dot pulse={value === 'executing'}>
      {value}
    </Badge>
  );
}

/** Approval status, coloured by `APPROVAL_STATUS_KINDS`; optional friendly `label` (raw kept as sr-only text, `title` and `data-status`). */
export function ApprovalStatusBadge({
  value,
  label,
  variant,
}: {
  value: ApprovalStatus;
  label?: ReactNode;
  variant?: BadgeVariant;
}) {
  return (
    <Badge
      {...(variant ? { variant } : {})}
      tone={STATUS_KIND_TONES[APPROVAL_STATUS_KINDS[value]]}
      mono={label === undefined}
      dot
      data-status={value}
      title={label === undefined ? undefined : value}
    >
      <Labelled label={label} raw={value} />
    </Badge>
  );
}
